import { useMutation } from '@urql/vue';

import { MARKETING_WORDING_VERSION } from '~/constants/marketingConsent';
import { marketingPreferenceQuery, recordMarketingConsentMutation } from '~/graphql/documents/core';

// The account page's marketing email choice. `optedIn` is null when the server does not offer the
// choice (school students, suppressed clusters, below the marketing age or no birth month on file).
// Disabled, it never asks the server (the lookup reads Braze) and offers nothing.
export const useMarketingPreference = async (enabled = true) => {
  // Every use* call goes before the await: the component instance is gone after it.
  const { $urqlClient } = useNuxtApp();
  const { executeMutation } = useMutation(recordMarketingConsentMutation);
  // A one-shot fetch that always settles. Awaiting a reactive useQuery here left the account page on
  // its loader every second time it was opened, because the awaited query never reported done.
  const result = enabled
    ? await $urqlClient.query(marketingPreferenceQuery, {}, { requestPolicy: 'network-only' }).toPromise()
    : null;

  const optedIn = ref<boolean | null>(result?.data?.core?.user?.self?.marketingPreference?.optedIn ?? null);

  // Bumped by every save, so a lookup that was already out when the student changed their choice
  // can tell its answer is stale.
  let saves = 0;

  // Asks the server again, for when something else on the page may have changed whether the choice
  // is offered. A failed lookup keeps the choice we know rather than hiding it.
  const refresh = async () => {
    const savesBefore = saves;
    const fresh = await $urqlClient.query(marketingPreferenceQuery, {}, { requestPolicy: 'network-only' }).toPromise();
    if (fresh.error || !fresh.data || saves !== savesBefore) {
      return;
    }
    optedIn.value = fresh.data.core?.user?.self?.marketingPreference?.optedIn ?? null;
  };

  // Shows the new choice straight away and puts the old one back if the server refuses it.
  const save = async (optIn: boolean) => {
    saves += 1;
    const previous = optedIn.value;
    optedIn.value = optIn;
    const { error } = await executeMutation({ input: { optIn, wordingVersion: MARKETING_WORDING_VERSION } });
    if (error) {
      optedIn.value = previous;
      return error;
    }
    return null;
  };

  return { optedIn, save, refresh };
};
